import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuCalendar, LuCalendarDays, LuCircleCheck, LuClock, LuListChecks, LuTrophy, LuUsers } from 'react-icons/lu';
import { ContestStateBadge } from './ContestStateBadge';
import { useBoundaryReload } from './useBoundaryReload';
import { Button, ButtonLink } from '../../ui/Button';
import { Countdown } from '../../ui/Countdown';
import { Alert, EmptyState, Skeleton } from '../../ui/Feedback';
import { useToast } from '../../ui/Toast';
import { useDocumentTitle, useStrings } from '../../tenant/TenantProvider';
import { useCatalog } from '../../stores/catalog';
import { contestApi } from '../../lib/contestApi';
import { formatCountdown, formatDate, formatDateTime, formatDuration, formatMonth } from '../../lib/format';
import { useNow } from '../../hooks/useNow';
import { cx } from '../../ui/cx';

function plainText(markdown) {
  return markdown
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[#>*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function DateBlock({ iso }) {
  const date = new Date(iso);
  return (
    <span className="date-block" aria-hidden="true">
      <span className="date-block__month">{formatMonth(iso)}</span>
      <span className="date-block__day tabular">{date.getDate()}</span>
    </span>
  );
}

function ContestAction({ contest, pending, onRegister, size, primary = true }) {
  const t = useStrings();
  const live = contest.state === 'live';
  if (live && contest.registered) {
    return (
      <ButtonLink to={`/contests/${contest.id}/arena`} variant="primary" size={size} trail={LuArrowRight}>
        {t('contests.enter')}
      </ButtonLink>
    );
  }
  if (contest.registered) {
    return (
      <span className="contest-registered">
        <LuCircleCheck aria-hidden="true" />
        {t('contests.registered')}
      </span>
    );
  }
  return (
    <Button variant={primary ? 'primary' : 'secondary'} size={size} loading={pending} onClick={() => onRegister(contest)}>
      {t('contests.register')}
    </Button>
  );
}

function ContestMeta({ contest }) {
  const t = useStrings();
  return (
    <p className="contest-meta">
      <span>
        <LuCalendar aria-hidden="true" />
        {formatDateTime(contest.startTime)}
      </span>
      <span>
        <LuClock aria-hidden="true" />
        {formatDuration(contest.durationMinutes)}
      </span>
      <span>
        <LuListChecks aria-hidden="true" />
        {t('contests.problemCount', { count: contest.problemCount })}
      </span>
      <span>
        <LuUsers aria-hidden="true" />
        {t('contests.registeredCount', { count: contest.participants })}
      </span>
    </p>
  );
}

function FeaturedContest({ contest, now, pending, onRegister }) {
  const t = useStrings();
  const live = contest.state === 'live';
  const remaining = Date.parse(live ? contest.endTime : contest.startTime) - now;
  return (
    <section className={cx('panel contest-feature lit energy energy--glow', live && 'is-live')} aria-labelledby="featured-contest">
      <div className="contest-feature__body">
        <div className="contest-feature__badges">
          <ContestStateBadge state={contest.state} />
          <span className="contest-feature__points">{t('contests.pointCount', { count: contest.totalPoints })}</span>
        </div>
        <h2 id="featured-contest" className="contest-feature__name">
          <Link to={`/contests/${contest.id}`} viewTransition>
            {contest.name}
          </Link>
        </h2>
        <ContestMeta contest={contest} />
        {contest.description && <p className="contest-feature__description">{plainText(contest.description)}</p>}
        <div className="contest-feature__actions">
          <ContestAction contest={contest} pending={pending} onRegister={onRegister} size="lg" />
          <ButtonLink to={`/contests/${contest.id}`} variant="ghost" size="lg" viewTransition>
            {t('contests.details')}
          </ButtonLink>
        </div>
      </div>
      <div className="contest-feature__clock">
        <span className="contest-feature__clock-label">{live ? t('contest.endsInLabel') : t('contest.startsInLabel')}</span>
        <Countdown ms={remaining} size="lg" />
        <span className="contest-feature__clock-note">
          {live ? t('contests.endsAt', { time: formatDateTime(contest.endTime) }) : t('contests.registrationCloses')}
        </span>
      </div>
    </section>
  );
}

function ContestRow({ contest, now, pending, onRegister }) {
  const t = useStrings();
  const live = contest.state === 'live';
  const remaining = Date.parse(live ? contest.endTime : contest.startTime) - now;

  return (
    <li className="contest-row lit">
      <DateBlock iso={contest.startTime} />
      <div className="contest-row__body">
        <div className="contest-row__title">
          <Link to={`/contests/${contest.id}`} className="contest-row__name" viewTransition>
            {contest.name}
          </Link>
          {live && <ContestStateBadge state="live" />}
        </div>
        <ContestMeta contest={contest} />
      </div>
      <div className="contest-row__side">
        <span className="contest-row__countdown mono">
          {t(live ? 'contests.endsIn' : 'contests.startsIn', { time: formatCountdown(remaining) })}
        </span>
        <ContestAction contest={contest} pending={pending} onRegister={onRegister} primary={false} />
      </div>
    </li>
  );
}

export function ContestsPage() {
  const t = useStrings();
  const navigate = useNavigate();
  const { show } = useToast();
  const now = useNow(1000);
  const contestsState = useCatalog((state) => state.contests);
  const [pendingId, setPendingId] = useState(null);
  const contests = contestsState.data;

  useDocumentTitle(t('contests.title'));

  const reload = useCallback(() => useCatalog.getState().loadContests(), []);

  useEffect(() => {
    reload();
  }, [reload]);

  const { current, past } = useMemo(() => {
    const list = contests ?? [];
    const active = list
      .filter((contest) => contest.state === 'live' || contest.state === 'upcoming')
      .sort((a, b) => (a.state === b.state ? Date.parse(a.startTime) - Date.parse(b.startTime) : a.state === 'live' ? -1 : 1));
    const finished = list
      .filter((contest) => contest.state === 'ended' || contest.state === 'finalized')
      .sort((a, b) => Date.parse(b.startTime) - Date.parse(a.startTime));
    return { current: active, past: finished };
  }, [contests]);

  const boundary = useMemo(() => {
    const times = current.map((contest) => Date.parse(contest.state === 'live' ? contest.endTime : contest.startTime));
    return times.length ? Math.min(...times) : null;
  }, [current]);
  useBoundaryReload(boundary, now, reload);

  const register = async (contest) => {
    setPendingId(contest.id);
    const response = await contestApi.register(contest.id);
    setPendingId(null);
    if (response.success) {
      useCatalog.getState().patchContest(contest.id, { registered: true, participants: contest.participants + 1 });
      show(t('contests.registeredToast', { name: contest.name }));
    } else {
      show(response.error, 'error');
      if (response.code === 'REGISTRATION_CLOSED' || response.code === 'ALREADY_REGISTERED') reload();
    }
  };

  const [featured, ...rest] = current;
  const loading = contestsState.status === 'loading' || contestsState.status === 'idle';

  return (
    <div className="page contests-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('contests.title')}</h1>
          <p className="page-subtitle">{t('contests.subtitle')}</p>
        </div>
      </header>

      {loading && (
        <div className="panel contest-feature" aria-hidden="true">
          <div className="contest-feature__body">
            <Skeleton width={120} />
            <Skeleton width="60%" height={40} style={{ marginTop: 20 }} />
            <Skeleton width="80%" style={{ marginTop: 16 }} />
            <Skeleton width={180} height={44} style={{ marginTop: 32 }} />
          </div>
          <div className="contest-feature__clock">
            <Skeleton width={240} height={64} />
          </div>
        </div>
      )}

      {contestsState.status === 'error' && (
        <Alert tone="danger" action={<Button size="sm" onClick={reload}>{t('common.retry')}</Button>}>
          {t('contests.loadError')} {contestsState.error}
        </Alert>
      )}

      {contestsState.status === 'ready' && (
        <>
          {featured ? (
            <FeaturedContest contest={featured} now={now} pending={pendingId === featured.id} onRegister={register} />
          ) : (
            <div className="panel contests-empty">
              <EmptyState icon={LuCalendarDays} title={t('contests.noUpcomingTitle')} text={t('contests.noUpcomingText')} />
            </div>
          )}

          {rest.length > 0 && (
            <section className="section">
              <h2 className="section-title">{t('contests.alsoUpcoming')}</h2>
              <ul className="contest-list">
                {rest.map((contest) => (
                  <ContestRow key={contest.id} contest={contest} now={now} pending={pendingId === contest.id} onRegister={register} />
                ))}
              </ul>
            </section>
          )}

          <section className="section">
            <h2 className="section-title">{t('contests.past')}</h2>
            {past.length === 0 ? (
              <p className="muted">{t('contests.noPastText')}</p>
            ) : (
              <div className="table-card">
                <table className="data-table data-table--interactive past-contests">
                  <thead>
                    <tr>
                      <th>{t('contests.columnContest')}</th>
                      <th>{t('contests.columnDate')}</th>
                      <th className="is-numeric">{t('contests.columnParticipants')}</th>
                      <th>{t('contests.columnWinner')}</th>
                    </tr>
                  </thead>
                  <tbody className="stagger">
                    {past.map((contest, index) => (
                      <tr
                        key={contest.id}
                        style={{ '--i': Math.min(index, 12) }}
                        onClick={() => navigate(`/contests/${contest.id}`, { viewTransition: true })}
                      >
                        <td>
                          <Link
                            to={`/contests/${contest.id}`}
                            className="past-contest__name"
                            viewTransition
                            onClick={(event) => event.stopPropagation()}
                          >
                            {contest.name}
                          </Link>
                          {contest.state === 'ended' && <ContestStateBadge state="ended" />}
                        </td>
                        <td className="muted">{formatDate(contest.startTime)}</td>
                        <td className="is-numeric muted">{contest.participants}</td>
                        <td>
                          {contest.winners[0] ? (
                            <span className="past-contest__winner">
                              <LuTrophy aria-hidden="true" />
                              {contest.winners[0].username}
                            </span>
                          ) : (
                            <span className="subtle">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
